import { Box, Typography, Button, Card, CardContent, Chip, Stack } from "@mui/material";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getFeaturedCaseStudies } from "../../../data/caseStudyIndex";
import { TagChip } from "../../../components/shared";
import { Reveal, useReveal } from "../../../components/Motion";

// Curated teaser pulled straight from the case-study data (its slim index, so
// Home does not load every study's narrative) so it never drifts out of sync
// with /work. Explicit order: corporate row (live demo first) then
// the personal founder stories - Supplier Data Console · Change Management /
// Neram · AESTA.
const FEATURED_ORDER = [
  "supplier-data-console",
  "change-management-console",
  "neram-classes",
  "aesta",
];
const featuredProjects = FEATURED_ORDER.map((id) =>
  getFeaturedCaseStudies().find((p) => p.id === id)
).filter(Boolean);

// Pick readable badge text for a given accent: dark ink on light accents
// (lemon yellow, teal), white on the darker ones (azure, magenta).
const readableOn = (hex) => {
  const h = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.substr(i, 2), 16) / 255);
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.6 ? "#16121f" : "#fff";
};

// A Card that reveals itself. The reveal sits on the Card, which is the grid
// item, so no wrapper stops it stretching to its row; hooks cannot run inside
// the map, hence the component.
const RevealCard = (props) => {
  const reveal = useReveal();
  return <Card ref={reveal.ref} {...reveal.props} {...props} />;
};

const FeaturedWork = () => {
  return (
    <Box
      data-nav-bg="dark"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 4, md: 5 },
        px: { xs: 2, md: 3 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      {/* Luxe pattern overlay - a fine designer's-canvas grid laid over the
          animated gradient (which still shows through unchanged), giving the
          glass cards a crisp texture to refract. No colour change. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.18) 1.2px, transparent 1.4px), " +
            "linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), " +
            "linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)",
          backgroundSize: "28px 28px, 28px 28px, 28px 28px",
          backgroundPosition: "14px 14px, 0 0, 0 0",
          maskImage:
            "radial-gradient(120% 85% at 50% 45%, #000 40%, transparent 88%)",
          WebkitMaskImage:
            "radial-gradient(120% 85% at 50% 45%, #000 40%, transparent 88%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "1080px",
        }}
      >
        {/* Section Header */}
        <Reveal
          sx={{
            textAlign: "center",
            mb: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant="sectionTitle"
            component="h2"
            sx={{
              color: "#fff",
              mb: 1,
              fontSize: { xs: "24px", md: "28px" },
              letterSpacing: { xs: "4px", md: "6px" },
            }}
          >
            Featured Work
          </Typography>
          <Typography
            sx={{
              fontFamily: "Kalam, cursive",
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: { xs: "14px", md: "16px" },
              lineHeight: 1.7,
              maxWidth: "500px",
              mx: "auto",
            }}
          >
            Try-it-live redesigns, deep product case studies, and interface
            craft. A taste of each.
          </Typography>
        </Reveal>

        {/* Projects Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(2, 1fr)",
            },
            gap: { xs: 2, md: 3 },
            mb: { xs: 3, md: 4 },
          }}
        >
          {featuredProjects.map((project) => {
            const accent = project.color;
            const isLive = project.hasDemo;
            return (
              <RevealCard
                key={project.id}
                component={Link}
                to={`/work/${project.id}`}
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.06) 100%)",
                  // The one glass that keeps a blur: the designer's-grid overlay
                  // behind these cards is real detail to refract. Lighter than it
                  // was (20px + 160%), since every pass re-runs as the page moves.
                  backdropFilter: "blur(12px) saturate(140%)",
                  WebkitBackdropFilter: "blur(12px) saturate(140%)",
                  border: "1px solid rgba(255, 255, 255, 0.20)",
                  borderRadius: "16px",
                  overflow: "hidden",
                  textDecoration: "none",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                  cursor: "pointer",
                  position: "relative",
                  boxShadow:
                    "0 10px 30px -8px rgba(10, 6, 32, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.28)",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow:
                      "0 18px 40px -10px rgba(10, 6, 32, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.34)",
                    border: "1px solid rgba(255, 255, 255, 0.30)",
                    "& .project-arrow": {
                      transform: "translateX(3px)",
                      opacity: 1,
                    },
                  },
                  // Accent top bar
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: accent,
                    zIndex: 1,
                  },
                  // Specular highlight - light catching the top-left of the glass
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background:
                      "radial-gradient(140px circle at 12% 0%, rgba(255,255,255,0.16), transparent 55%)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 2.5 }, position: "relative", zIndex: 1 }}>
                  {/* Highlight Badge + Live pill */}
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mb: 1.5 }}
                  >
                    <Chip
                      label={project.highlightBadge}
                      size="small"
                      sx={{
                        backgroundColor: accent,
                        color: readableOn(accent),
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "10px",
                        fontWeight: 600,
                        height: "22px",
                        "& .MuiChip-label": { px: 1.5 },
                      }}
                    />
                    {isLive && (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          height: "22px",
                          px: 1,
                          borderRadius: "11px",
                          border: `1px solid ${accent}`,
                          color: "#fff",
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "10px",
                          fontWeight: 600,
                          letterSpacing: "0.4px",
                          textTransform: "uppercase",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: accent,
                            boxShadow: `0 0 8px ${accent}`,
                            "@keyframes featLivePulse": {
                              "0%, 100%": { opacity: 1 },
                              "50%": { opacity: 0.35 },
                            },
                            "@media (prefers-reduced-motion: no-preference)": {
                              animation: "featLivePulse 1.8s ease-in-out infinite",
                            },
                          }}
                        />
                        Live
                      </Box>
                    )}
                  </Stack>

                  {/* Title */}
                  <Typography
                    component="h3"
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontWeight: 600,
                      fontSize: { xs: "16px", md: "17px" },
                      color: "#fff",
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      lineHeight: 1.3,
                    }}
                  >
                    {project.subtitle || project.title}
                    <ArrowForwardIcon
                      className="project-arrow"
                      sx={{
                        fontSize: "16px",
                        opacity: 0.4,
                        transition: "transform 0.3s ease, opacity 0.3s ease",
                        flexShrink: 0,
                        ml: 1,
                      }}
                    />
                  </Typography>

                  {/* Description */}
                  <Typography
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: { xs: "12px", md: "13px" },
                      color: "rgba(255, 255, 255, 0.82)",
                      mb: 1.5,
                      lineHeight: 1.5,
                      minHeight: { xs: "auto", md: "54px" },
                    }}
                  >
                    {project.description}
                  </Typography>

                  {/* Tags */}
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {project.tags.slice(0, 3).map((tag) => (
                      <TagChip key={tag} label={tag} sx={{ height: "22px" }} />
                    ))}
                  </Stack>
                </CardContent>
              </RevealCard>
            );
          })}
        </Box>

        {/* View All CTA */}
        <Reveal sx={{ textAlign: "center" }}>
          <Link to="/work" style={{ textDecoration: "none" }}>
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon sx={{ fontSize: "16px" }} />}
              sx={{
                color: "#fff",
                borderColor: "rgba(255, 255, 255, 0.3)",
                fontWeight: 500,
                fontSize: "13px",
                px: 3,
                py: 1,
                borderRadius: "6px",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              View All Projects
            </Button>
          </Link>
        </Reveal>
      </Box>
    </Box>
  );
};

export default FeaturedWork;
